// src/components/PaymentGate.tsx

import { useState, useEffect } from "react";
import { fetchAuthSession } from "aws-amplify/auth";
import OnboardingProgressBar from "./OnboardingProgressBar";
import PaymentForm from "./PaymentForm";
import { ZodiacSign } from "@/utils/zodiac";
import WaitWithHopeModal from "./WaitWithHopeModal";
import { gqlUpdateUser, gqlGetUser, updateCrushListVault } from "./UserData";
import { getAmplifyCustomOutput } from "../lib/environment";

// ============================================================================
// Constants
// ============================================================================

const PRICE = 7.95; // $7.95 one-time lifetime payment

// TODO: Set YEARS_GRANTED back to 1 after lifetime promotional period ends
//const YEARS_GRANTED = 100;

// ============================================================================
// Helpers
// ============================================================================

/**
 * Parse crushListVaultEnc and mark all existing crushes as needing reconfirmation.
 * Returns the updated crushes array and the count of existing crushes.
 */
function parseAndMarkCrushesForReconfirm(
  crushListVaultEnc: string | null | undefined
): { updatedCrushes: any[]; count: number } {
  if (!crushListVaultEnc) return { updatedCrushes: [], count: 0 };

  try {
    const crushData = typeof crushListVaultEnc === 'string'
      ? JSON.parse(crushListVaultEnc)
      : crushListVaultEnc;

    const crushes = Array.isArray(crushData) ? crushData : (crushData?.items || []);
    const updatedCrushes = crushes.map((crush: any) => ({ ...crush, needsReconfirm: true }));
    return { updatedCrushes, count: crushes.length };
  } catch {
    return { updatedCrushes: [], count: 0 };
  }
}

// ============================================================================
// Types
// ============================================================================

interface PaymentGateProps {
  userId: string;
  onComplete: (scrollToCrushes?: boolean) => Promise<void>;
  zodiacSign?: ZodiacSign | null;
  onCancel?: () => void;
}

// ============================================================================
// Component
// ============================================================================

const PaymentGate = ({ userId, onComplete, zodiacSign, onCancel }: PaymentGateProps) => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [error,              setError]              = useState<string | null>(null);
  const [success,            setSuccess]            = useState(false);
  const [isProcessing,       setIsProcessing]       = useState(false);
  const [hasExistingCrushes, setHasExistingCrushes] = useState(false);
  const [btnHov,   setBtnHov]   = useState<string | null>(null);
  const [btnPress, setBtnPress] = useState<string | null>(null);
  void hasExistingCrushes; // set by payment/promo paths, read path removed with success screen copy update

  // Promo code
  const [promoCode,         setPromoCode]         = useState("");
  const [promoError,        setPromoError]        = useState<string | null>(null);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);

  // --------------------------------------------------------------------------
  // Promo code
  // validatePromoCode Lambda only validates + marks code used.
  // PaymentGate must write the tier upgrade for promo path.
  // --------------------------------------------------------------------------

  const handleApplyPromoCode = async () => {
    if (!promoCode.trim()) return;

    setIsValidatingPromo(true);
    setPromoError(null);

    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const idToken = session.tokens?.idToken?.toString();

      if (!idToken) {
        setPromoError('Not authenticated. Please sign in again.');
        return;
      }

      const apiUrl = getAmplifyCustomOutput('PaymentAPIEndpoint');
      if (!apiUrl) {
        setPromoError('API configuration error. Please refresh the page.');
        return;
      }

      const response = await fetch(`${apiUrl}validate-promo-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          code: promoCode.trim().toUpperCase(),
          // NOTE: userId intentionally omitted — backend derives from auth token
        }),
      });

      if (!response.ok) {
        setPromoError('Failed to validate code. Please try again.');
        return;
      }

      const result = await response.json();

      if (!result.valid) {
        setPromoError(result.error || "Invalid or expired promo code");
        return;
      }

      // Valid promo — upgrade the user
      setIsProcessing(true);
      try {
        // All tiers are lifetime. Sentinel value 2099 used for entitlement gate compatibility.
        const planExpiresAt = '2099-12-31T23:59:59.000Z';

        const currentUser = await gqlGetUser(userId);
        const { updatedCrushes, count } = parseAndMarkCrushesForReconfirm(
          currentUser?.crushListVaultEnc
        );

        const updateInput: any = {
          id: userId,
          pricePaidCents: 0,
          promoCodeUsed: promoCode.trim().toUpperCase(),
          planExpiresAt,
          tier: "paid",
          hasSeenTierChoice: true,
        };

        await gqlUpdateUser(updateInput);

        // Vault write goes through the encrypted REST Lambda; not AppSync.
        if (count > 0) {
          const apiBase = apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`;
          await updateCrushListVault(apiBase, `Bearer ${idToken}`, updatedCrushes);
        }

        setHasExistingCrushes(count > 0);
        window.scrollTo({ top: 0, behavior: "smooth" });
        setSuccess(true);
        setError(null);

      } catch (err) {
        setError("Failed to apply promo code. Please contact support.");
      } finally {
        setIsProcessing(false);
      }

    } catch (err) {
      setPromoError("Failed to validate code. Please try again.");
    } finally {
      setIsValidatingPromo(false);
    }
  };

  // --------------------------------------------------------------------------
  // Payment
  // processPayment Lambda handles the tier upgrade authoritatively (tier, 
  // pricePaidCents, planExpiresAt, handle verification triggering).
  // Here we only refresh the user record and mark crushes for reconfirm.
  // --------------------------------------------------------------------------

  const handlePaymentSuccess = async (paymentDetails: any) => {
    void ["Payment successful:", paymentDetails];
    setIsProcessing(true);

    try {
      // Lambda already wrote tier/price/expires — just refresh and handle crushes
      const currentUser = await gqlGetUser(userId);
      const { updatedCrushes, count } = parseAndMarkCrushesForReconfirm(
        currentUser?.crushListVaultEnc
      );

      const updateInput: any = {
        id: userId,
        hasSeenTierChoice: true,
      };

      await gqlUpdateUser(updateInput);

      // Vault write goes through the encrypted REST Lambda; not AppSync.
      if (count > 0) {
        try {
          const session = await fetchAuthSession({ forceRefresh: true });
          const idToken = session.tokens?.idToken?.toString();
          const apiUrl = getAmplifyCustomOutput('PaymentAPIEndpoint');
          if (idToken && apiUrl) {
            const apiBase = apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`;
            await updateCrushListVault(apiBase, `Bearer ${idToken}`, updatedCrushes);
          }
        } catch {
          // non-fatal
        }
      }

      setHasExistingCrushes(count > 0);
      window.scrollTo({ top: 0, behavior: "smooth" });
      setSuccess(true);
      setError(null);

    } catch (err) {
      setError("Payment processed but account update failed. Please contact support.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePaymentFailure = (errorMessage: string) => {
    setError(errorMessage);
    setSuccess(false);
  };

  const handleContinue = async () => {
    await onComplete(false);
  };

  const handleContinueWithCrushes = async () => {
    await onComplete(true);
  };

  // --------------------------------------------------------------------------
  // Success screen
  // --------------------------------------------------------------------------

  if (success) {
    return (
      <WaitWithHopeModal
        mode="fullscreen"
        zodiacSign={zodiacSign ?? null}
        onAddCrush={handleContinueWithCrushes}
        onDismiss={handleContinue}
      />
    );
  }

  // --------------------------------------------------------------------------
  // Payment form
  // --------------------------------------------------------------------------

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "#F0DFC8" }}
    >
      <div className="w-full mb-12 max-w-2xl">

        {/* Cancel — top left */}
        {onCancel && (
          <div className="flex justify-start mb-2">
            <button
              onClick={onCancel}
              onMouseEnter={() => setBtnHov('cancelTop')}
              onMouseLeave={() => { setBtnHov(null); setBtnPress(null); }}
              onMouseDown={() => setBtnPress('cancelTop')}
              onMouseUp={() => setBtnPress(null)}
              style={{
                fontFamily: "'Fredoka', sans-serif",
                fontSize: 16,
                color: "#1A1A1A",
                background: btnPress === 'cancelTop' ? "rgba(0,0,0,0.12)" : btnHov === 'cancelTop' ? "rgba(0,0,0,0.06)" : "#FFFFFF",
                border: "3px solid #1A1A1A",
                borderRadius: 100,
                boxShadow: "4px 4px 0px #1A1A1A",
                padding: "10px 24px",
                cursor: "pointer",
                transform: btnPress === 'cancelTop' ? "scale(0.97)" : "scale(1)",
                transition: "all 0.1s ease",
              }}
            >
              <span style={{ fontSize: 20, marginRight: 4 }}>&#8249;</span> Back
            </button>
          </div>
        )}
        <OnboardingProgressBar
          currentStep={3}
          steps={["Phone Verification", "Age Verification", "Payment", "Done"]}
        />

        <h2
          style={{
            fontFamily: "'Fredoka', sans-serif",
            fontSize: 28,
            fontWeight: 700,
            color: "#1A1A1A",
            textAlign: "center",
            marginTop: 0,
            marginBottom: 4,
          }}
        >
          Unlock Matching
        </h2>
        <p style={{
          fontFamily: "'Nunito', sans-serif",
          fontSize: 28,
          fontWeight: 800,
          color: "#c0645a",
          textAlign: "center",
          marginTop: 4,
          marginBottom: 4,
        }}>
          $7.95
        </p>
        <p style={{
          fontFamily: "'Nunito', sans-serif",
          fontSize: 13,
          color: "#888",
          textAlign: "center",
          marginTop: 0,
          marginBottom: 20,
        }}>
          One-time. Yours forever.
        </p>

        {/* Value summary */}
        <div style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          margin: "16px 0 24px 0",
          padding: "16px 20px",
          background: "#f5f0eb",
          borderRadius: 10,
        }}>
          {[
            "See if anyone likes you back",
            "Unlimited matches for life",
            "One-time payment · No subscription",
          ].map((line) => (
            <div key={line} style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              fontFamily: "'Nunito', sans-serif",
              fontSize: 14,
              color: "#333",
              fontWeight: 600,
            }}>
              <span style={{ color: "#c0645a", fontWeight: 800, fontSize: 16 }}>✓</span>
              {line}
            </div>
          ))}
        </div>

        {/* Promo Code */}
        <div
          style={{
            background: "#FFFFFF",
            border: "2.5px solid #1A1A1A",
            borderRadius: 16,
            boxShadow: "4px 4px 0px #1A1A1A",
            padding: 16,
            marginBottom: 20,
            boxSizing: "border-box",
          }}
        >
          <label
            style={{
              display: "block",
              fontFamily: "'Fredoka', sans-serif",
              fontSize: 16,
              fontWeight: 600,
              color: "#1A1A1A",
              marginBottom: 8,
            }}
          >
            Have a promo code?
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              type="text"
              value={promoCode}
              onChange={(e) => {
                setPromoCode(e.target.value.toUpperCase());
                setPromoError(null);
              }}
              placeholder="Enter code"
              disabled={isValidatingPromo || isProcessing}
              style={{
                flex: 1,
                border: "2px solid #E05A3A",
                borderRadius: 100,
                padding: "10px 16px",
                fontFamily: "'Nunito', sans-serif",
                fontSize: 15,
                outline: "none",
                background: "#FDF6EE",
                boxSizing: "border-box",
              }}
            />
            <button
              onClick={handleApplyPromoCode}
              disabled={!promoCode.trim() || isValidatingPromo || isProcessing}
              onMouseEnter={() => !(!promoCode.trim() || isValidatingPromo || isProcessing) && setBtnHov('apply')}
              onMouseLeave={() => { setBtnHov(null); setBtnPress(null); }}
              onMouseDown={() => !(!promoCode.trim() || isValidatingPromo || isProcessing) && setBtnPress('apply')}
              onMouseUp={() => setBtnPress(null)}
              style={{
                background: (btnPress === 'apply') ? "#B34428" : (btnHov === 'apply') ? "#C94E30" : "#E05A3A",
                color: "#FFFFFF",
                border: "3px solid #1A1A1A",
                borderRadius: 100,
                boxShadow: "4px 4px 0px #1A1A1A",
                padding: "10px 20px",
                fontFamily: "'Fredoka', sans-serif",
                fontSize: 16,
                fontWeight: 600,
                cursor: (!promoCode.trim() || isValidatingPromo || isProcessing) ? "default" : "pointer",
                opacity: (!promoCode.trim() || isValidatingPromo || isProcessing) ? 0.65 : 1,
                transform: btnPress === 'apply' ? "scale(0.97)" : "scale(1)",
                transition: "all 0.1s ease",
              }}
            >
              {isValidatingPromo ? "..." : "Apply"}
            </button>
          </div>
          {promoError && (
            <p
              style={{
                marginTop: 8,
                marginBottom: 0,
                fontFamily: "'Nunito', sans-serif",
                fontSize: 13,
                color: "#C44B2E",
              }}
            >
              {promoError}
            </p>
          )}
        </div>

        {error && (
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              background: "#FFF0F0",
              border: "1px solid rgba(196, 75, 46, 0.3)",
              borderRadius: 12,
            }}
          >
            <p
              style={{
                margin: 0,
                fontFamily: "'Nunito', sans-serif",
                fontSize: 13,
                color: "#C44B2E",
              }}
            >
              {error}
            </p>
          </div>
        )}

        {isProcessing && (
          <div
            style={{
              marginBottom: 16,
              padding: 12,
              background: "#F0F4FF",
              border: "1px solid rgba(60, 100, 200, 0.25)",
              borderRadius: 12,
            }}
          >
            <p
              style={{
                margin: 0,
                fontFamily: "'Nunito', sans-serif",
                fontSize: 13,
                color: "#3C64C8",
              }}
            >
              Processing your payment and updating your account...
            </p>
          </div>
        )}

        <PaymentForm
          amount={PRICE}
          onSuccess={handlePaymentSuccess}
          onFailure={handlePaymentFailure}
        />

        {/* Cancel — below pay button */}
        {onCancel && (
          <button
            onClick={onCancel}
            onMouseEnter={() => setBtnHov('cancelBot')}
            onMouseLeave={() => { setBtnHov(null); setBtnPress(null); }}
            onMouseDown={() => setBtnPress('cancelBot')}
            onMouseUp={() => setBtnPress(null)}
            style={{
              width: "100%",
              marginTop: 12,
              borderRadius: 100,
              padding: "12px 24px",
              fontFamily: "'Fredoka', sans-serif",
              fontSize: 16,
              fontWeight: 600,
              color: "#1A1A1A",
              border: "3px solid #1A1A1A",
              background: btnPress === 'cancelBot' ? "rgba(0,0,0,0.12)" : btnHov === 'cancelBot' ? "rgba(0,0,0,0.06)" : "#FFFFFF",
              cursor: "pointer",
              boxShadow: "4px 4px 0px #1A1A1A",
              boxSizing: "border-box",
              transform: btnPress === 'cancelBot' ? "scale(0.97)" : "scale(1)",
              transition: "all 0.1s ease",
            }}
          >
            Maybe Later
          </button>
        )}
      </div>
    </div>
  );
};

export default PaymentGate;